import * as React from 'react';
import { ReactElement } from 'react';
import { createStyles, Grid, makeStyles, TextField } from '@material-ui/core';
import {
  ConnectCommunityUserType,
  ConnectEverywearStatus,
  ConnectUserType,
  UserStatus,
} from 'types/member';
import { CheckObjectValue } from 'types/common';
import {
  CONNECT_ABUSING_USER,
  connectCommunityUserTypeOption,
  connectEverywearStatusOption,
  connectUserTypeOption,
  userStatusOption,
} from 'constants/member/connect';
import MultipleCheckboxFilter from 'components/common/searchfilter/MultipleCheckboxFilter';
import MultipleSelectFilter from 'components/common/searchfilter/MultipleSelectFilter';
import FilterElementLayout from 'components/common/searchfilter/FilterElementLayout';
import MemberSeachFilterLayout from './memberSearchFilterLayout';
import { TextWrapper } from 'components/common/searchfilter/FilterElement';

interface Props {
  keyword: string;
  userStatus: CheckObjectValue;
  onChangeKeyword: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDownKeyword: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  connectAbusingUserStatus: CheckObjectValue;
  connectEverywearStatus: CheckObjectValue;
  connectUserType: CheckObjectValue;
  connectCommunityUserType: CheckObjectValue;
  onChangeConnectAbusingUser: (values: string[]) => void;
  onChangeConnectEverywearStatus: (values: ConnectEverywearStatus[]) => void;
  onChangeConnectUserType: (values: ConnectUserType[]) => void;
  onChangeConnectCommunityUserType: (values: ConnectCommunityUserType[]) => void;
  onChangeUserStatus: (values: UserStatus[]) => void;
  onClickSearch: () => void;
  onClickReset: () => void;
}

const SearchFilter = ({
  keyword,
  userStatus,
  onChangeKeyword,
  onKeyDownKeyword,
  connectAbusingUserStatus,
  connectEverywearStatus,
  connectUserType,
  connectCommunityUserType,
  onChangeConnectEverywearStatus,
  onChangeConnectAbusingUser,
  onChangeConnectUserType,
  onChangeConnectCommunityUserType,
  onChangeUserStatus,
  onClickSearch,
  onClickReset,
}: Props): ReactElement => {
  const connectAbusingUserCheckboxes = Object.entries(connectAbusingUserStatus).map(
    ([key, value]) => {
      return value ? key : '';
    },
  );

  const userStatusCheckboxes = Object.entries(userStatus)
    .filter(([_userType, isChecked]) => isChecked)
    .map(([userType, _isChecked]) => Number(userType));

  const connectEverywearStatusCheckboxes = Object.entries(connectEverywearStatus)
    .filter(([_, value]) => value)
    .map(([key, _]) => Number(key));

  const connectUserTypes = Object.entries(connectUserType)
    .filter(([_userType, isChecked]) => isChecked)
    .map(([userType, _isChecked]) => Number(userType));

  const connectCommunityUserTypes = Object.entries(connectCommunityUserType)
    .filter(([_userType, isChecked]) => isChecked)
    .map(([userType, _isChecked]) => Number(userType));

  const handleChangeConnectAbusing = (selectedValues: string[]): void => {
    onChangeConnectAbusingUser(selectedValues);
  };

  const handleChangeEverywearStatus = (selectedValues: ConnectEverywearStatus[]): void => {
    onChangeConnectEverywearStatus(selectedValues);
  };

  const classes = makeStyles(() =>
    createStyles({
      root: {
        width: '30%',
      },
    }),
  )();

  return (
    <MemberSeachFilterLayout getSearchResult={onClickSearch} resetFilter={onClickReset}>
      <Grid>
        <Grid item xs={12}>
          <FilterElementLayout label={'Keyword'} gridSize={1}>
            <TextField
              classes={classes}
              placeholder="Email, External, Group ID, Domain"
              value={keyword}
              onChange={onChangeKeyword}
              onKeyDown={onKeyDownKeyword}
            />
          </FilterElementLayout>
        </Grid>
      </Grid>

      <Grid>
        <Grid item xs={12}>
          <MultipleCheckboxFilter<UserStatus>
            statuses={userStatusCheckboxes}
            onChangeChecked={onChangeUserStatus}
            filterLabel={'User Status'}
            checkedOptions={userStatusOption}
            gridSize={1}
          />
        </Grid>
      </Grid>

      <Grid>
        <Grid item xs={12}>
          <FilterElementLayout
            label={'Connect'}
            layoutStyles={{ alignItems: 'baseline' }}
            gridSize={1}
          >
            <Grid container direction={'row'}>
              <Grid item xs={6}>
                <MultipleCheckboxFilter<string>
                  statuses={connectAbusingUserCheckboxes}
                  onChangeChecked={(values: string[]) => handleChangeConnectAbusing(values)}
                  filterLabel={'Abusing User'}
                  checkedOptions={CONNECT_ABUSING_USER}
                />
              </Grid>

              <Grid item xs={6}>
                <MultipleCheckboxFilter<ConnectEverywearStatus>
                  statuses={connectEverywearStatusCheckboxes}
                  onChangeChecked={(values: ConnectEverywearStatus[]) =>
                    handleChangeEverywearStatus(values)
                  }
                  filterLabel={'EveryWear Status'}
                  checkedOptions={connectEverywearStatusOption}
                />
              </Grid>
            </Grid>

            <Grid container direction={'row'}>
              <Grid item xs={6}>
                <MultipleSelectFilter<ConnectUserType>
                  label={'User Type'}
                  values={connectUserTypes}
                  options={connectUserTypeOption}
                  onChange={onChangeConnectUserType}
                  width={'200px'}
                />
              </Grid>

              <Grid item xs={6}>
                <MultipleSelectFilter<ConnectCommunityUserType>
                  label={
                    <TextWrapper fontWeight={'600'} textAlign={'left'}>
                      Community
                      <br />
                      User Type
                    </TextWrapper>
                  }
                  values={connectCommunityUserTypes}
                  options={connectCommunityUserTypeOption}
                  onChange={onChangeConnectCommunityUserType}
                  width={'200px'}
                />
              </Grid>
            </Grid>
          </FilterElementLayout>
        </Grid>
      </Grid>
    </MemberSeachFilterLayout>
  );
};

export default SearchFilter;
